import { useState } from "react";
import {
  Box,
  FormControlLabel,
  Checkbox,
  TextField,
  Button,
  Chip,
  Typography,
  Stack,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";

export const COMMON_DISEASES = [
  "Артериальная гипертензия",
  "Нарушения сна",
  "Сахарный диабет",
  "Гипотиреоз",
  "Инсульт",
  "Сосудистые нарушения головного мозга",
  "Нарушение слуха",
  "Депрессия",
  "Онкология",
  "COVID-19",
  "Инфаркт миокарда",
  "Атеросклероз",
];

interface Props {
  value: string[];
  onChange: (diseases: string[]) => void;
  disabled?: boolean;
}

export default function DiseasesSelector({ value, onChange, disabled }: Props) {
  const [otherOpen, setOtherOpen] = useState(false);
  const [otherText, setOtherText] = useState("");

  const selected = value || [];
  const selectedSet = new Set(selected);
  const customDiseases = selected.filter((d) => !COMMON_DISEASES.includes(d));

  const toggle = (disease: string) => {
    if (selectedSet.has(disease)) {
      onChange(selected.filter((d) => d !== disease));
    } else {
      onChange([...selected, disease]);
    }
  };

  const addOther = () => {
    const text = otherText.trim();
    if (!text) return;
    if (selectedSet.has(text)) {
      setOtherText("");
      setOtherOpen(false);
      return;
    }
    onChange([...selected, text]);
    setOtherText("");
    setOtherOpen(false);
  };

  const removeCustom = (disease: string) => {
    onChange(selected.filter((d) => d !== disease));
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Заболевания
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 0 }}>
        {COMMON_DISEASES.map((disease) => (
          <FormControlLabel
            key={disease}
            control={
              <Checkbox
                checked={selectedSet.has(disease)}
                onChange={() => toggle(disease)}
                disabled={disabled}
                size="small"
              />
            }
            label={<Typography variant="body2">{disease}</Typography>}
          />
        ))}
      </Box>

      {customDiseases.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}>
          {customDiseases.map((disease) => (
            <Chip
              key={disease}
              label={disease}
              size="small"
              onDelete={disabled ? undefined : () => removeCustom(disease)}
              deleteIcon={<CloseIcon />}
              color="primary"
              variant="outlined"
            />
          ))}
        </Stack>
      )}

      <Box sx={{ mt: 1.5 }}>
        {!otherOpen ? (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setOtherOpen(true)}
            disabled={disabled}
          >
            Другое
          </Button>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "row", gap: 1, alignItems: "center" }}>
            <TextField
              size="small"
              placeholder="Другое заболевание"
              value={otherText}
              onChange={(e) => setOtherText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addOther();
                }
              }}
              disabled={disabled}
              autoFocus
              sx={{ flex: 1 }}
            />
            <Button size="small" variant="contained" onClick={addOther} disabled={disabled}>
              Добавить
            </Button>
            <Button
              size="small"
              onClick={() => {
                setOtherOpen(false);
                setOtherText("");
              }}
              disabled={disabled}
            >
              Отмена
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
